import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { WaveDivider } from "@/components/WaveDivider";
import { TrustStrip } from "@/components/TrustStrip";
import { SEOHead } from "@/components/SEOHead";
import { FAQSection } from "@/components/FAQSection";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/lib/i18n";
import { getLocalizedFAQ } from "@/lib/faqContent";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { getFixedPrice } from "@/lib/fixedPrices";
import { getAllArticles, getArticleContent } from "@/lib/blogContent";
import { BookOpen, ArrowRight, Clock } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import iconGlassSkin from "@/assets/icon-glass-skin.png";
import iconHydration from "@/assets/icon-hydration.png";
import iconAntiAging from "@/assets/icon-anti-aging.png";
import iconScience from "@/assets/icon-science.png";

/* ── Color constants for wave dividers ── 
   Must match the section-gradient endpoints in index.css */
const GRADIENT_TOP = "#EEF1F9";    /* --section-from: start of gradient sections */
const GRADIENT_BOTTOM = "#F5FAFD"; /* --section-to: end of gradient sections */
const WHITE = "#FFFFFF";

export default function Index() {
  const { t, language, currency } = useLanguage();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const faqItems = getLocalizedFAQ(language);

  // Build ItemList for SEO (product carousel in search results)
  const productHandles = ['pink-glow-serum-with-pdrn-peptides', 'pdrn-pink-sheet-masks-10-pack', 'smart-capsule-cream-with-collagen', 'complete-pdrn-trio-bundle'];
  const itemList = productHandles.map((handle, idx) => {
    const fp = getFixedPrice(handle, currency || 'USD');
    return {
      name: t(`seo.productTitle.${handle}`) !== `seo.productTitle.${handle}` ? t(`seo.productTitle.${handle}`) : handle,
      url: `https://pdrn-skin.com/product/${handle}`,
      price: fp?.sale,
      currency: currency || 'USD',
      position: idx + 1,
    };
  });

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await fetchProducts(10);
        setProducts(data);
      } catch (err) {
        setError('Failed to load products');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t('seo.homeTitle')}
        description={t('seo.homeDescription')}
        isHomePage
        faqItems={faqItems}
        itemList={itemList}
      />
      <Header />
      <main>
        <Hero />

        <TrustStrip />

        {/* Wave: Hero (gradient bottom) → Why PDRN (white) */}
        <WaveDivider topColor={GRADIENT_BOTTOM} bottomColor={WHITE} variant="wave1" />

        <AboutSection />

        <WaveDivider topColor={WHITE} bottomColor={GRADIENT_TOP} variant="wave2" />

        <ScienceSection />

        <WaveDivider topColor={GRADIENT_BOTTOM} bottomColor={WHITE} variant="wave3" />

        <ProductsSection products={products} isLoading={isLoading} error={error} />

        <WaveDivider topColor={WHITE} bottomColor={GRADIENT_TOP} variant="wave1" />

        {/* FAQ Section */}
        <FAQSection />

        <WaveDivider topColor={GRADIENT_BOTTOM} bottomColor={WHITE} variant="wave2" />

        {/* Blog Section */}
        <HomeBlogSection />

        {/* Wave: Blog (white) → Footer (gradient) */}
        <WaveDivider topColor={WHITE} bottomColor={GRADIENT_TOP} variant="wave3" />
      </main>

      <Footer />
    </div>
  );
}

/* ── Scroll-revealed sub-sections ── */

function AboutSection() {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  return (
    <section id="about" className="py-14 md:py-16 bg-white relative" aria-label="About PDRN">
      <div ref={ref} className={`container scroll-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-5">
            {t('about.pdrnTitle').split('PDRN')[0]}<span className="text-primary">PDRN</span>{t('about.pdrnTitle').split('PDRN')[1] || ''}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('about.pdrnDescription')}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {([
            { icon: iconGlassSkin, alt: 'Glass skin effect icon', title: t('feature.glassSkin'), desc: t('about.glassSkinDesc') },
            { icon: iconHydration, alt: 'Hydration serum icon', title: t('feature.hydration'), desc: t('about.hydrationDesc') },
            { icon: iconAntiAging, alt: 'Anti-aging collagen icon', title: t('feature.antiAging'), desc: t('about.antiAgingDesc') },
          ]).map((item, i) => (
            <article key={i} className="text-center p-8 bg-white/80 backdrop-blur-sm rounded-3xl shadow-soft border border-border/50 hover:shadow-md transition-shadow">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-secondary to-pink-200 flex items-center justify-center p-2">
                <img src={item.icon} alt={item.alt} className="w-10 h-10 object-contain" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScienceSection() {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  return (
    <section className="py-14 md:py-16 bg-section-gradient" aria-label="Scientific Research">
      <div ref={ref} className={`container max-w-3xl scroll-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-secondary to-pink-200 flex items-center justify-center">
            <img src={iconScience} alt="" className="w-8 h-8" aria-hidden="true" />
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">{t('science.title')}</h2>
          <p className="text-muted-foreground">{t('science.subtitle')}</p>
        </div>
        <div className="space-y-6">
          {[
            { citation: t('science.citation1'), source: t('science.source1') },
            { citation: t('science.citation2'), source: t('science.source2') },
          ].map((item, i) => (
            <blockquote key={i} className="science-citation p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-border/50 shadow-soft">
              <div className="flex gap-3 items-start">
                <BookOpen className="w-5 h-5 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <p className="text-sm md:text-base italic text-foreground leading-relaxed mb-2">{item.citation}</p>
                  <cite className="text-xs text-muted-foreground not-italic font-medium">{item.source}</cite>
                </div>
              </div>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductsSection({ products, isLoading, error }: { products: ShopifyProduct[]; isLoading: boolean; error: string | null }) {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  return (
    <section id="products" className="py-14 md:py-16 bg-white relative" aria-label="Products">
      <div ref={ref} className={`container scroll-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-5">{t('products.title')}</h2>
          <p className="text-muted-foreground">{t('products.subtitle')}</p>
        </div>
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="rounded-2xl border border-border overflow-hidden bg-card">
                <Skeleton className="aspect-square w-full" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-8 w-1/3 mt-4" />
                  <Skeleton className="h-10 w-full mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 text-destructive">{error}</div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">{t('products.noProducts')}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.node.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HomeBlogSection() {
  const { t, language } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const articles = getAllArticles().slice(0, 3);

  return (
    <section className="py-14 md:py-16 bg-white" aria-label="Blog">
      <div ref={ref} className={`container scroll-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">{t('blog.latestArticles')}</h2>
          <p className="text-muted-foreground">{t('blog.latestSubtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {articles.map(article => {
            const content = getArticleContent(article.slug, language);
            if (!content) return null;
            return (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group block rounded-2xl border border-border/50 bg-card overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                {article.image && (
                  <img
                    src={article.image}
                    alt={content.title}
                    className="w-full h-44 object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-5 flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <time dateTime={article.date}>
                      {new Date(article.date).toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </time>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" aria-hidden="true" />
                      {article.readTime} {t('blog.minRead')}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold font-serif text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2">
                    {content.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{content.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary mt-1 group-hover:gap-2 transition-all">
                    {t('blog.readMore')}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
          >
            {t('blog.viewAll')}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
